/**
 * Emotional signal extraction.
 *
 * Derives qualitative signals about an announcement from the data we already
 * have (message, template, relationship, photos, time of day). These signals
 * steer style + theme picking so two reveals in the same category can still
 * feel emotionally distinct.
 */

export type EmotionalTone =
  | 'warm'
  | 'reflective'
  | 'celebratory'
  | 'apologetic'
  | 'romantic'
  | 'closure'
  | 'legacy'
  | 'gratitude'
  | 'healing';

export type EmotionalIntensity = 'gentle' | 'moderate' | 'intense';
export type IntimacyLevel = 'close' | 'familial' | 'broad';
export type EnergyLevel = 'still' | 'flowing' | 'uplifting';
export type TimeOfDay = 'dawn' | 'day' | 'dusk' | 'night';
export type PhotoMood = 'none' | 'few' | 'many';

export interface EmotionalSignals {
  tone: EmotionalTone;
  intensity: EmotionalIntensity;
  intimacy: IntimacyLevel;
  energy: EnergyLevel;
  timeOfDay: TimeOfDay;
  photoMood: PhotoMood;
  messageLength: number;
}

export interface SignalInput {
  message?: string;
  template?: string;
  relationship?: string;
  photoUrls?: string[];
  publishedDate?: string;
}

const TEMPLATE_TONE: Record<string, EmotionalTone> = {
  love: 'romantic',
  romantic: 'romantic',
  gratitude: 'gratitude',
  apology: 'apologetic',
  closure: 'closure',
  hard_conversation: 'reflective',
  celebration: 'celebratory',
  birthday: 'celebratory',
  anniversary: 'romantic',
  encouragement: 'healing',
  legacy: 'legacy',
  memorial: 'legacy',
  custom: 'warm',
};

const KEYWORD_TONE: Array<{ tone: EmotionalTone; words: RegExp }> = [
  { tone: 'apologetic', words: /\b(sorry|forgive|apolog|regret|wish I had)\b/i },
  { tone: 'gratitude', words: /\b(thank|grateful|appreciate|gratitude)\b/i },
  { tone: 'romantic', words: /\b(love you|my love|forever yours|soulmate|heart)\b/i },
  { tone: 'celebratory', words: /\b(birthday|congratulations|celebrate|cheers|hooray|bravo)\b/i },
  { tone: 'closure', words: /\b(goodbye|farewell|peace|closure|let go)\b/i },
  { tone: 'legacy', words: /\b(legacy|generation|carry on|remember me|inheritance)\b/i },
  { tone: 'healing', words: /\b(heal|hope|courage|strength|you can|believe in you)\b/i },
  { tone: 'reflective', words: /\b(realize|reflect|looking back|years ago|when I think)\b/i },
];

const FAMILIAL = /(mom|mother|dad|father|sister|brother|son|daughter|grandma|grandpa|aunt|uncle|cousin|family)/i;
const CLOSE = /(spouse|wife|husband|partner|love|fianc|boyfriend|girlfriend)/i;

export function deriveEmotionalSignals(input: SignalInput): EmotionalSignals {
  const message = input.message ?? '';
  const lower = message.toLowerCase();

  // Tone: template first, then keyword override if a strong cue is present.
  let tone: EmotionalTone = TEMPLATE_TONE[(input.template ?? '').toLowerCase()] ?? 'warm';
  for (const { tone: t, words } of KEYWORD_TONE) {
    if (words.test(message)) {
      tone = t;
      break;
    }
  }

  // Intensity from punctuation density + caps + length of strongest sentence.
  const exclam = (message.match(/[!?]/g) ?? []).length;
  const capsWords = (message.match(/\b[A-Z]{3,}\b/g) ?? []).length;
  const intensityScore = exclam + capsWords * 2 + (message.length > 800 ? 2 : 0);
  const intensity: EmotionalIntensity =
    intensityScore >= 6 ? 'intense' : intensityScore >= 2 ? 'moderate' : 'gentle';

  // Intimacy from relationship.
  const rel = (input.relationship ?? '').toString();
  const intimacy: IntimacyLevel = CLOSE.test(rel) || CLOSE.test(lower)
    ? 'close'
    : FAMILIAL.test(rel) || FAMILIAL.test(lower)
      ? 'familial'
      : 'broad';

  // Energy from tone + intensity.
  const energy: EnergyLevel =
    tone === 'celebratory' || tone === 'healing'
      ? 'uplifting'
      : tone === 'closure' || tone === 'legacy' || tone === 'reflective'
        ? 'still'
        : 'flowing';

  // Time of day — local hour (recipient's), falls back to evening.
  const hour = (() => {
    try {
      return new Date().getHours();
    } catch {
      return 19;
    }
  })();
  const timeOfDay: TimeOfDay =
    hour < 6 ? 'night' : hour < 10 ? 'dawn' : hour < 17 ? 'day' : hour < 21 ? 'dusk' : 'night';

  const photoCount = input.photoUrls?.filter(Boolean).length ?? 0;
  const photoMood: PhotoMood = photoCount === 0 ? 'none' : photoCount <= 3 ? 'few' : 'many';

  return {
    tone,
    intensity,
    intimacy,
    energy,
    timeOfDay,
    photoMood,
    messageLength: message.length,
  };
}
